
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface ScrollRestorationOptions {
  enabled?: boolean;
  scrollBehavior?: ScrollBehavior;
}

/**
 * A hook that restores scroll position when navigating
 * @param options Configuration options for scroll restoration
 */
const useScrollRestoration = (options: ScrollRestorationOptions = {}) => {
  const { enabled = true, scrollBehavior = 'smooth' } = options;
  const location = useLocation();

  useEffect(() => {
    if (!enabled) return;

    // Don't auto-scroll on the Kontakt page when tab parameters are present
    if (location.pathname === '/kontakt' && location.search.includes('tab=')) {
      return;
    }

    // On the Blog page, only skip scroll-to-top for search/filter changes (same pathname)
    // but DO scroll to top when navigating TO /blog from another page
    if (location.pathname === '/blog' && location.search) {
      return;
    }

    // Special handling for the Preise page with hash
    if (location.pathname === '/preise' && location.hash) {
      setTimeout(() => {
        const targetId = location.hash.substring(1);
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
          const headerOffset = 120; // Adjusted based on actual header height
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: scrollBehavior,
          });
        }
      }, 700); // Increased timeout to ensure rendering is complete
      return;
    }
    
    // Restore scroll position when location changes
    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: scrollBehavior,
      });
    };

    // Check if there's a hash in the URL
    if (location.hash) {
      // Delay the scroll to ensure the DOM is fully loaded
      setTimeout(() => {
        const element = document.getElementById(location.hash.substring(1));
        if (element) {
          // Account for header height with a more generous offset
          const headerOffset = 120; // Adjusted based on actual header height
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: scrollBehavior,
          });
        }
      }, 700); // Increased timeout to ensure rendering is complete
    } else {
      scrollToTop();
    }
  }, [location, enabled, scrollBehavior]);
};

export default useScrollRestoration;
